import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Typography,
  Paper,
} from '@mui/material';
import { CloudUpload, Save, Cancel } from '@mui/icons-material';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrlWithFallback } from '../../utils/imageUrlHelper';

interface FeaturedApp {
  _id: string;
  name: string;
  description: string;
  iconUrl: string;
  androidUrl?: string;
  iosUrl?: string;
  isActive: boolean;
  displayOrder: number;
}

interface FeaturedAppFormProps {
  app: FeaturedApp | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  description: string;
  iconUrl: string;
  androidUrl: string;
  iosUrl: string;
  isActive: boolean;
  displayOrder: number;
}

const FeaturedAppForm: React.FC<FeaturedAppFormProps> = ({
  app,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    iconUrl: '',
    androidUrl: '',
    iosUrl: '',
    isActive: true,
    displayOrder: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingIcon, setUploadingIcon] = useState(false);

  useEffect(() => {
    if (app) {
      setFormData({
        name: app.name,
        description: app.description,
        iconUrl: app.iconUrl,
        androidUrl: app.androidUrl || '',
        iosUrl: app.iosUrl || '',
        isActive: app.isActive,
        displayOrder: app.displayOrder,
      });
    }
  }, [app]);

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingIcon(true);
      const uploadFormData = new FormData();
      // IMPORTANT: Append folder BEFORE file so multer can read it during file processing
      uploadFormData.append('folder', 'featured-apps');
      uploadFormData.append('file', file);

      const response = await apiService.uploadImage(uploadFormData);
      if (response.success && response.data?.url) {
        setFormData((prev) => ({
          ...prev,
          iconUrl: response.data!.url,
        }));
        toast.success('Icon uploaded successfully');
      } else {
        toast.error(response.error || 'Failed to upload icon');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('App name is required');
      return false;
    }

    if (!formData.description.trim()) {
      setError('Description is required');
      return false;
    }

    if (!formData.iconUrl.trim()) {
      setError('App icon is required');
      return false;
    }

    if (!formData.androidUrl.trim() && !formData.iosUrl.trim()) {
      setError('At least one platform URL (Android or iOS) is required');
      return false;
    }

    // Validate URLs
    if (formData.androidUrl && !isValidUrl(formData.androidUrl)) {
      setError('Invalid Android URL');
      return false;
    }

    if (formData.iosUrl && !isValidUrl(formData.iosUrl)) {
      setError('Invalid iOS URL');
      return false;
    }

    return true;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        iconUrl: formData.iconUrl.trim(),
        androidUrl: formData.androidUrl.trim() || undefined,
        iosUrl: formData.iosUrl.trim() || undefined,
        isActive: formData.isActive,
        displayOrder: formData.displayOrder,
      };

      let response;
      if (app) {
        // Update existing app
        response = await apiService.updateFeaturedApp(app._id, payload);
      } else {
        // Create new app
        response = await apiService.createFeaturedApp(payload);
      }

      if (response.success) {
        toast.success(
          app ? 'Featured app updated successfully' : 'Featured app created successfully'
        );
        onSuccess();
      } else {
        setError(response.error || 'Failed to save featured app');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save featured app');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* App Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="App Name"
            value={formData.name}
            onChange={handleChange('name')}
            inputProps={{ maxLength: 100 }}
            helperText={`${formData.name.length}/100 characters`}
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            inputProps={{ maxLength: 500 }}
            helperText={`${formData.description.length}/500 characters`}
          />
        </Grid>

        {/* Icon Upload */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            App Icon *
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              component="label"
              startIcon={uploadingIcon ? <CircularProgress size={20} /> : <CloudUpload />}
              disabled={uploadingIcon}
            >
              {uploadingIcon ? 'Uploading...' : 'Upload Icon'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleIconUpload}
              />
            </Button>
            {formData.iconUrl && (
              <Paper elevation={1} sx={{ p: 1, display: 'inline-block' }}>
                <Box
                  component="img"
                  src={getImageUrlWithFallback(formData.iconUrl)}
                  alt="App icon preview"
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 1,
                    display: 'block',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder-icon.png';
                  }}
                />
              </Paper>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Recommended: 512x512px PNG or JPG, max 5MB
          </Typography>
        </Grid>

        {/* Android URL */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Android Store URL"
            value={formData.androidUrl}
            onChange={handleChange('androidUrl')}
            placeholder="https://play.google.com/store/apps/details?id=..."
            helperText="Google Play Store URL for Android users"
          />
        </Grid>

        {/* iOS URL */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="iOS Store URL"
            value={formData.iosUrl}
            onChange={handleChange('iosUrl')}
            placeholder="https://apps.apple.com/app/id..."
            helperText="Apple App Store URL for iOS users"
          />
        </Grid>

        {/* Display Order */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Display Order"
            value={formData.displayOrder}
            onChange={handleChange('displayOrder')}
            inputProps={{ min: 0 }}
            helperText="Lower numbers appear first"
          />
        </Grid>

        {/* Active Status */}
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange('isActive')}
                color="primary"
              />
            }
            label="Active"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Only active apps will be visible in the mobile app
          </Typography>
        </Grid>

        {/* Form Actions */}
        <Grid item xs={12}>
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || uploadingIcon}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            >
              {loading ? 'Saving...' : app ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeaturedAppForm;
